import React, { Component } from "react";
import PropTypes from "prop-types";

import BarSeries from "./BarSeries";
import LineSeries from "./LineSeries";
import StraightLine from "./StraightLine";

class RVISeries extends Component {
  static yExtents(datum, index, allData, indicator) {
    const dataArr = Array.isArray(allData)
      ? allData
      : Array.isArray(datum)
        ? datum
        : [datum];

    const key = indicator?.renderKey;

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    dataArr.forEach((item) => {
      if (!item) return;

      // Check RVI values
      let rviValue, signalValue, histValue;

      if (key && item[key]) {
        const rviData = item[key];
        if (typeof rviData.rvi === "number") rviValue = rviData.rvi;
        if (typeof rviData.signal === "number") signalValue = rviData.signal;
        if (typeof rviData.histogram === "number")
          histValue = rviData.histogram;
      } else if (item.rvi) {
        if (typeof item.rvi.rvi === "number") rviValue = item.rvi.rvi;
        if (typeof item.rvi.signal === "number") signalValue = item.rvi.signal;
        if (typeof item.rvi.histogram === "number")
          histValue = item.rvi.histogram;
      }

      // Update min/max with all values
      [rviValue, signalValue, histValue].forEach((v) => {
        if (typeof v === "number" && Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      });
    });

    // Ensure zero is always included for RVI oscillator
    if (min > 0) min = 0;
    if (max < 0) max = 0;

    return [min, max];
  }

  constructor(props) {
    super(props);
    this.yAccessorForRVI = this.yAccessorForRVI.bind(this);
    this.yAccessorForSignal = this.yAccessorForSignal.bind(this);
    this.yAccessorForHistogram = this.yAccessorForHistogram.bind(this);
    this.yAccessorForHistogramBase = this.yAccessorForHistogramBase.bind(this);
  }

  yAccessorForRVI(d) {
    const { yAccessor } = this.props;
    return yAccessor(d) && yAccessor(d).rvi;
  }

  yAccessorForSignal(d) {
    const { yAccessor } = this.props;
    return yAccessor(d) && yAccessor(d).signal;
  }

  yAccessorForHistogram(d) {
    const { yAccessor } = this.props;
    return yAccessor(d) && yAccessor(d).histogram;
  }

  yAccessorForHistogramBase(xScale, yScale /* , d */) {
    return yScale(0);
  }

  render() {
    const { className, widthRatio, onDoubleClick, onContextMenu, yAccessor } =
      this.props;
    const { strokeRVI, strokeSignal, strokeZeroLine, barHistogram } =
      this.props;
    const { clip } = this.props;

    // Ensure barHistogram has default values for backward compatibility
    const safeBarHistogram = barHistogram || {};
    const barWidth = safeBarHistogram.width ?? 1;
    const barOpacity = safeBarHistogram.opacity ?? 0.6;

    // Support both old format (fillColor) and new format (increasingColor/decreasingColor)
    const increasingColor = safeBarHistogram.increasingColor ?? "#8cc176";
    const decreasingColor =
      safeBarHistogram.decreasingColor ??
      safeBarHistogram.fillColor ??
      "#b82c0c";

    // Create fill function that determines color based on histogramDirection
    const fillFunction = (d) => {
      const rviData = yAccessor(d);
      if (!rviData || !rviData.histogramDirection) {
        return decreasingColor;
      }
      return rviData.histogramDirection === "up"
        ? increasingColor
        : decreasingColor;
    };

    return (
      <g className={className}>
        {/* Histogram bars */}
        <BarSeries
          baseAt={this.yAccessorForHistogramBase}
          className="rvi-histogram"
          width={barWidth}
          widthRatio={widthRatio}
          stroke={true}
          fill={fillFunction}
          opacity={barOpacity}
          clip={clip}
          yAccessor={this.yAccessorForHistogram}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        />

        {/* Zero line */}
        <StraightLine stroke={strokeZeroLine} yValue={0} />

        {/* Main RVI line */}
        <LineSeries
          yAccessor={this.yAccessorForRVI}
          stroke={strokeRVI}
          fill="none"
          className="rvi-line"
          clip={clip}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        />

        {/* Signal line */}
        <LineSeries
          yAccessor={this.yAccessorForSignal}
          stroke={strokeSignal}
          fill="none"
          className="rvi-signal"
          clip={clip}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        />
      </g>
    );
  }
}

RVISeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  clip: PropTypes.bool.isRequired,
  strokeRVI: PropTypes.object.isRequired,
  strokeSignal: PropTypes.object.isRequired,
  strokeZeroLine: PropTypes.object.isRequired,
  barHistogram: PropTypes.object.isRequired,
  widthRatio: PropTypes.number,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

RVISeries.defaultProps = {
  className: "react-stockcharts-rvi-series",
  clip: true,
  widthRatio: 0.5,
};

export default RVISeries;
